import React from "react";
import get from "lodash/get";
import isEqual from "lodash/isEqual";
import { connect } from "react-redux";
import {
  Alert,
  Layout,
  Table,
  Breadcrumb,
  Select,
  Form,
  Button,
  Modal,
} from "antd";
import find from "lodash/find";
import sortBy from "lodash/sortBy";

import AdminHeader from "../../components/AdminHeader/AdminHeader";
import CopySitePermissionsForm from "../../components/CopySitePermissionsForm/CopySitePermissionsForm";
import CopyUserPermissionsForm from "../../components/CopyUserPermissionsForm/CopyUserPermissionsForm";
import CopyUserPermissionsMultiSitesForm from "../../components/CopyUserPermissionsMultiSitesForm/CopyUserPermissionsMultiSitesForm";
import Footer from "../../components/Footer/Footer";

import classes from "./SettingsUsersBulk.css";

import isAuthenticated from "../../utils/isAuthenticated";
import {
  getUsersLight,
  getSettingsUsersRequest,
  getSitesRequest,
  getRolesLight,
  updateUserRoleRequest,
  createUserRoleRequest,
  getSettingsUserSingleRequest,
  toggleUserActivateRoleRequest,
} from "../../api/requests";
import { logout } from "../../reducers/user/actions";
import {
  getUserCreatePermission,
  getUserUpdatePermission,
  getUserDeletePermission,
  getUserTimezone,
  getUserShortTimezone,
} from "../../reducers/user/selectors";

const { Content } = Layout;

const SiteCell = React.memo(
  ({
     user,
     site,
     loading,
     form,
     updateUserRole,
     findUserSiteAccess,
     rolesList,
   }) => {
    const siteAccess = findUserSiteAccess({
      user,
      site,
    });

    return form.getFieldDecorator(`user_${user.id}_site_${site.id}`, {
      initialValue:
        get(siteAccess, "state") === "active"
          ? get(siteAccess, "user_role.id")
          : null,
    })(
      <Select
        className={classes.roleSelect}
        loading={loading ? loading.isLoading : false}
        disabled={loading ? loading.isLoading : false}
        onChange={(value) =>
          updateUserRole({
            user: user,
            siteAccessId: siteAccess ? siteAccess.id : null,
            site: site,
            roleId: value,
          })
        }
      >
        <Select.Option value={null}>&nbsp;</Select.Option>
        {rolesList.map((role) => (
          <Select.Option value={role.id} key={role.id}>
            {get(siteAccess, "user_role.id") === role.id &&
            get(siteAccess, "state") === "inactive" ? (
              <span className={classes.inactiveRole}>
                {role.name} - Status: Inactive
              </span>
            ) : (
              role.name
            )}
          </Select.Option>
        ))}
      </Select>
    );
  },
  (prevProps, nextProps) => {
    const userNotUpdated = isEqual(prevProps.user, nextProps.user);

    const loadingNotUpdated = isEqual(prevProps.loading, nextProps.loading);

    return userNotUpdated && loadingNotUpdated;
  }
);

class SettingsUsersBulk extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      usersList: [],
      rolesList: [],
      errorMessage: "",
      responseMessage: "",
      userEdit: null,
      userTrainingEdit: null,
      currentPage: 1,
      total: 1,
      pageSize: 10,
      sites: [],
      isLoading: false,
      isInitialLoading: true,
      loadingItems: [],
      sitesFilter: [],
      users: [],
      userFilter: null,
      siteCells: [],
      isCopySiteModal: false,
      isCopyUserModal: false,
      isCopyUserMultiModal: false,
    };
  }

  async componentDidMount() {
    this.getData();

    const { data: users } = await getUsersLight();
    this.setState({ users });
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevState.sitesFilter.length !== this.state.sitesFilter.length ||
      prevState.sites.length !== this.state.sites.length
    ) {
      this.generateSiteCells();
    }
  }

  generateSiteCells = () => {
    const filteredSites =
      this.state.sitesFilter.length === 0
        ? this.state.sites
        : this.state.sites.filter((item) =>
          this.state.sitesFilter.includes(item.id)
        );

    const siteCells = sortBy(filteredSites, "id").map((item) => ({
      dataIndex: `site_${item.id}`,
      key: `site_${item.id}`,
      title: item.name,
      render: (val, record) => {
        const itemLoading = find(
          this.state.loadingItems,
          (loadingItem) =>
            loadingItem.userId === record.id && loadingItem.siteId === item.id
        );

        return (
          <SiteCell
            {...{
              user: record,
              site: item,
              loading: itemLoading,
              form: this.props.form,
              updateUserRole: this.updateUserRole,
              findUserSiteAccess: this.findUserSiteAccess,
              rolesList: this.state.rolesList,
            }}
          />
        );
      },
    }));

    this.setState({
      siteCells,
    });
  };

  getData = async () => {
    try {
      const [{ data }] = await Promise.all([
        getSitesRequest(),
        this.getUsersList(),
        this.getRolesList(),
      ]);

      this.setState({ sites: data, isInitialLoading: false });
    } catch (e) {
      if (get(e, "response.status") === 401) {
        this.props.logout();
        return;
      }
      console.log(e);
    }
  };

  columns() {
    return [
      {
        dataIndex: "user_id",
        key: "user_id",
        title: "User",
        render: (val, record) => (
          <div className={classes.userColWrapper}>
            <span className={classes.userCol}>
              {record.first_name} {record.last_name} {record.email} (ID {val})
            </span>
          </div>
        ),
      },
      ...this.state.siteCells,
    ];
  }

  findUserSiteAccess = ({ user, site }) =>
    find(user.site_access_list, (siteAccess) => siteAccess.site.id === site.id);

  updateUserRole = async ({ user, siteAccessId, site, roleId }) => {
    if (
      this.state.loadingItems.findIndex(
        (item) => item.userId === user.id && item.siteId === site.id
      ) > -1
    ) {
      this.updateLoadingItem({
        userId: user.id,
        siteId: site.id,
        isLoading: true,
      });
    } else {
      this.setState({
        loadingItems: [
          ...this.state.loadingItems,
          {
            userId: user.id,
            siteId: site.id,
            isLoading: true,
          },
        ],
      });
    }

    try {
      if (!roleId) {
        await toggleUserActivateRoleRequest(user.id, siteAccessId, "active");
      } else if (!siteAccessId) {
        await createUserRoleRequest(user.id, {
          site_id: site.id,
          user_role_id: roleId,
        });
      } else {
        await updateUserRoleRequest(user.id, siteAccessId, {
          user_id: user.id,
          site_id: site.id,
          user_role_id: roleId,
        });

        const {
          data: { data: users },
        } = await getSettingsUsersRequest({
          page: this.state.currentPage,
          per_page: this.state.pageSize,
        });

        const refetchedUser = find(users, (item) => item.id === user.id);

        if (refetchedUser) {
          const siteAccess = this.findUserSiteAccess({
            user: refetchedUser,
            site: site,
          });

          if (get(siteAccess, "state") === "inactive") {
            await toggleUserActivateRoleRequest(
              user.id,
              siteAccessId,
              "inactive"
            );
          }
        }
      }

      const { data: refetchedUser } = await getSettingsUserSingleRequest(
        user.id
      );

      this.setState({
        usersList: this.state.usersList.map((item) =>
          item.id === user.id
            ? { ...item, site_access_list: refetchedUser.site_access_list }
            : item
        ),
      });
    } catch (e) {
      if (get(e, "response.status") === 401) {
        this.props.logout();
        return;
      }

      this.setState({
        subject: null,
        errorMessage: get(e, "response.data.error", "Error"),
      });
    } finally {
      this.updateLoadingItem({
        userId: user.id,
        siteId: site.id,
        isLoading: false,
      });
    }
  };

  updateLoadingItem = ({ userId, siteId, isLoading }) => {
    this.setState({
      loadingItems: this.state.loadingItems.map((item) =>
        item.userId === userId && item.siteId === siteId
          ? { ...item, isLoading }
          : item
      ),
    });
  };

  getRolesList = async () => {
    this.setState({
      errorMessage: null,
      isLoading: true,
      responseMessage: null,
    });

    try {
      const { data } = await getRolesLight();
      this.setState({ rolesList: data });
    } catch (e) {
      if (get(e, "response.status") === 401) {
        this.props.logout();
        return;
      }
      this.setState({
        subject: null,
        errorMessage: get(e, "response.data.error", "Error"),
      });
    }

    this.setState({ isLoading: false });
  };

  getUsersList = async (
    page = this.state.currentPage,
    per_page = this.state.pageSize
  ) => {
    this.setState({
      errorMessage: null,
      isLoading: true,
      responseMessage: null,
    });

    try {
      const { data } = await getSettingsUsersRequest({
        page,
        per_page,
        filter_by: this.state.userFilter ? "user" : null,
        user_id: this.state.userFilter,
      });
      this.setState({
        usersList: data.data,
        total: data.total_pages * per_page,
        currentPage: page,
      });
    } catch (e) {
      if (get(e, "response.status") === 401) {
        this.props.logout();
        return;
      }
      this.setState({
        subject: null,
        errorMessage: get(e, "response.data.error", "Error"),
      });
    }

    this.setState({ isLoading: false });
  };

  handleTableChange = (pagination, filters, sorter) => {
    this.setState(
      {
        isLoading: true,
        pageSize: pagination.pageSize,
        sortedInfo: sorter,
      },
      () => this.getUsersList(pagination.current, pagination.pageSize)
    );
  };

  onCopySiteShow = () => {
    this.setState({ isCopySiteModal: true });
  };

  onCopySiteHide = () => {
    this.setState({ isCopySiteModal: false });
  };

  onCopySiteSubmit = () => {
    this.getData();
    this.onCopySiteHide();
  };

  onCopyUserShow = () => {
    this.setState({ isCopyUserModal: true });
  };

  onCopyUserHide = () => {
    this.setState({ isCopyUserModal: false });
  };

  onCopyUserSubmit = () => {
    this.getData();
    this.onCopyUserHide();
  };

  onCopyUserMultiShow = () => {
    this.setState({ isCopyUserMultiModal: true });
  };

  onCopyUserMultiHide = () => {
    this.setState({ isCopyUserMultiModal: false });
  };

  onCopyUserMultiSubmit = () => {
    this.getData();
    this.onCopyUserMultiHide();
  };

  render() {
    const {
      usersList,
      errorMessage,
      responseMessage,
      isLoading,
      currentPage,
      total,
      pageSize,
      isInitialLoading,
      isCopySiteModal,
      isCopyUserModal,
      isCopyUserMultiModal,
      rolesList,
      sites,
      users,
    } = this.state;

    return (
      <div style={{ backgroundColor: "#FAFAFA" }}>
        <Layout className={classes.parentContainer}>
          <AdminHeader />
          <Content className={classes.content}>
            <h3 className="headline-default">Bulk User Management</h3>
            <Breadcrumb>
              <Breadcrumb.Item href="/admin/settings">
                Admin Settings
              </Breadcrumb.Item>
              <Breadcrumb.Item>Bulk User Management</Breadcrumb.Item>
            </Breadcrumb>
            <br />

            {errorMessage && <Alert type="error" message={errorMessage} />}

            {responseMessage && (
              <Alert type="success" message={responseMessage} />
            )}

            <div className={classes.siteSearchContainer}>
              <b className={classes.searchWrapperLabel}>Sites Filter</b>
              <Select
                mode="multiple"
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option.props.children
                    .toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
                }
                className={classes.siteSearchSelect}
                onChange={(value) =>
                  this.setState({
                    sitesFilter: value,
                  })
                }
                placeholder="Select Sites"
              >
                {this.state.sites.map((site) => (
                  <Select.Option key={site.id} value={site.id}>
                    {`${site.code} - ${site.name}`}
                  </Select.Option>
                ))}
              </Select>
            </div>

            <div className={classes.siteSearchContainer}>
              <b className={classes.searchWrapperLabel}>User Filter</b>
              <Select
                showSearch
                optionFilterProp="children"
                className={classes.siteSearchSelect}
                onChange={(value) =>
                  this.setState(
                    () => ({
                      userFilter: value,
                    }),
                    () => {
                      this.getUsersList();
                    }
                  )
                }
                placeholder="Select User"
              >
                <Select.Option value={null}>None</Select.Option>
                {users.map((users) => (
                  <Select.Option key={users.id} value={users.id}>
                    {users.username}
                  </Select.Option>
                ))}
              </Select>
            </div>

            <div className={classes.copyContainer}>
              <Button type="primary" onClick={this.onCopySiteShow}>
                Copy site permissions
              </Button>
              &nbsp;
              <Button type="primary" onClick={this.onCopyUserShow}>
                Copy user permissions
              </Button>
              &nbsp;
              <Button type="primary" onClick={this.onCopyUserMultiShow}>
                Add/remove user permission to multiple sites
              </Button>
            </div>

            {/*<div className={classes.tableContainerWrapper}>
              <div className={classes.tableContainerUser}>

              </div>
            </div>*/}

            <div className={classes.tableContainer}>
              <Table
                columns={this.columns()}
                dataSource={usersList}
                loading={isLoading || isInitialLoading}
                rowKey="id"
                onChange={this.handleTableChange}
                pagination={{
                  onShowSizeChange: (current, size) => {
                    this.setState({ pageSize: size });
                  },
                  current: currentPage,
                  total,
                  pageSize,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "25", "50", "100"],
                }}
              />
            </div>

            <Modal
              visible={!!isCopySiteModal}
              onCancel={this.onCopySiteHide}
              footer={null}
            >
              {isCopySiteModal && (
                <CopySitePermissionsForm
                  onCancel={this.onCopySiteHide}
                  onSubmit={this.onCopySiteSubmit}
                />
              )}
            </Modal>

            <Modal
              visible={!!isCopyUserModal}
              onCancel={this.onCopyUserHide}
              footer={null}
            >
              {isCopyUserModal && (
                <CopyUserPermissionsForm
                  onCancel={this.onCopyUserHide}
                  onSubmit={this.onCopyUserSubmit}
                />
              )}
            </Modal>
            <Modal
              visible={!!isCopyUserMultiModal}
              onCancel={this.onCopyUserMultiHide}
              footer={null}
            >
              {isCopyUserMultiModal && (
                <CopyUserPermissionsMultiSitesForm
                  sitesList={sites}
                  rolesList={rolesList}
                  onCancel={this.onCopyUserMultiHide}
                  onSubmit={this.onCopyUserMultiSubmit}
                />
              )}
            </Modal>
          </Content>
          <Footer noAdminLink />
        </Layout>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  userTimezone: getUserTimezone(state),
  userShortTimezone: getUserShortTimezone(state),
  userCreatePermission: getUserCreatePermission(state),
  userDeletePermission: getUserDeletePermission(state),
  userUpdatePermission: getUserUpdatePermission(state),
});

const mapDispatchToProps = { logout };

export default Form.create({ name: "test" })(
  isAuthenticated(
    connect(mapStateToProps, mapDispatchToProps)(SettingsUsersBulk)
  )
);



// WEBPACK FOOTER //
// ./src/containers/SettingsUsersBulk/SettingsUsersBulk.js